/**
 * Sidereal time and the meridian
 *
 * GMST from the IAU 1982/2006 series, apparent sidereal time via the nutation in
 * longitude, LMST for an observer, and RAMC — the right ascension of the midheaven,
 * which is what the quadrant house systems actually need.
 *
 * Longitudes east of Greenwich are positive, everywhere in this codebase.
 *
 * © Manish Jagdish Thatte
 */

import { e_tilt } from 'astronomy-engine';
import { J2000, julianCenturiesJ2000, julianDayTT } from './julian.js';
import { timeFromTT } from './ephemeris.js';

const norm360 = (d) => ((d % 360) + 360) % 360;
const D2R = Math.PI / 180;

/**
 * Greenwich **Mean** Sidereal Time in degrees, from JD_UT.
 *
 * Meeus eq. 12.4. ⚠ The rate is 360.98564736629°/day, not 360° — a sidereal day
 * is shorter than a solar one, and using 360 puts the ascendant out by about
 * one degree per day of elapsed time.
 *
 * ⚠ Takes **UT**, not TT. Sidereal time measures the Earth's rotation, which is
 * what UT is defined by; using TT here would put the meridian out by ΔT, which
 * is 15″ of arc today and a great deal more for a 19th-century birth.
 */
export function gmst(jdUT) {
  if (!Number.isFinite(jdUT)) throw new TypeError('gmst: jdUT must be finite');
  const d = jdUT - J2000;
  const T = d / 36525;
  return norm360(
    280.46061837
    + 360.98564736629 * d
    + 0.000387933 * T * T
    - (T * T * T) / 38710000
  );
}

/**
 * The equation of the equinoxes in degrees: Δψ · cos ε, the difference between
 * apparent and mean sidereal time. Reaches about 1.1 s of time (16″ of arc).
 */
export function equationOfEquinoxes(jdUT) {
  const tilt = e_tilt(timeFromTT(julianDayTT(jdUT)));
  return (tilt.dpsi / 3600) * Math.cos(tilt.mobl * D2R);
}

/**
 * Greenwich **Apparent** Sidereal Time in degrees — mean plus the equation of
 * the equinoxes. This is the one the ascendant needs: the true equinox of date
 * is the origin the ecliptic longitudes in this codebase are measured from, so
 * the meridian has to be measured from the same place.
 */
export function apparentSiderealTime(jdUT) {
  return norm360(gmst(jdUT) + equationOfEquinoxes(jdUT));
}

/**
 * Local Mean Sidereal Time in degrees. ◆ East longitude is positive, so it is
 * added. ⚠ JHora's `.jhd` files store east as negative; the sign is flipped at
 * that boundary, in tools/jhora-fixtures.mjs, and nowhere else.
 */
export function lmst(jdUT, lonEast) {
  requireLongitude(lonEast);
  return norm360(gmst(jdUT) + lonEast);
}

/** Local Apparent Sidereal Time in degrees. */
export function last(jdUT, lonEast) {
  requireLongitude(lonEast);
  return norm360(apparentSiderealTime(jdUT) + lonEast);
}

/**
 * RAMC — the right ascension of the midheaven, in degrees.
 *
 * It is exactly the local apparent sidereal time: the meridian's right
 * ascension is what sidereal time measures. The separate name is kept because
 * that is what the house formulae call it, and because writing `ramc` at the
 * call site makes the *apparent* choice above impossible to lose.
 */
export function ramc(jdUT, lonEast) {
  return last(jdUT, lonEast);
}

function requireLongitude(lonEast) {
  if (!Number.isFinite(lonEast)) throw new TypeError('longitude must be finite');
  if (lonEast < -180 || lonEast > 180) {
    throw new RangeError(`longitude ${lonEast} out of range — east is positive, −180…180`);
  }
}

/** True obliquity of the ecliptic in degrees, the ε the house formulae use. */
export function trueObliquity(jdUT) {
  return e_tilt(timeFromTT(julianDayTT(jdUT))).tobl;
}
